import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDateTime } from '../contexts/DateTimeContext';
import { useTask } from '../contexts/TaskContext';
import { PRIORITIES, formatDuration } from '../lib/constants';
import { getDefaultFields } from '../lib/viewFields';

/**
 * TimelineView - Gantt-style timeline with left task list + right timeline grid.
 * Pattern follows Asana/ClickUp Gantt: sticky left panel with task names, scrollable
 * timeline on the right with day grid, today indicator, weekend stripes, and bars
 * positioned by scheduled_start/end (or due_date + estimated duration).
 */

const ZOOM_PRESETS = {
  month: { dayWidth: 32, labelKey: 'planner.month_view' },
  week: { dayWidth: 80, labelKey: 'planner.week_view' },
  day: { dayWidth: 160, labelKey: 'planner.day_view' },
};

const ROW_HEIGHT = 40;
const HEADER_HEIGHT = 56;
const TASK_PANEL_WIDTH = 280;

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const TimelineView = ({
  tasks = [],
  projects = [],
  onTaskClick,
  visibleFields = null,
  customFieldDefs = [],
}) => {
  const { t } = useTranslation();
  const { weekdayLetters, language } = useDateTime();
  const { openTaskDetail } = useTask();
  const scrollRef = useRef(null);
  const [zoom, setZoom] = useState('week');
  const dayWidth = ZOOM_PRESETS[zoom].dayWidth;

  const fields = useMemo(() => {
    if (Array.isArray(visibleFields)) return new Set(visibleFields);
    return new Set([...getDefaultFields('timeline'), ...customFieldDefs.map(d => `cf_${d.id}`)]);
  }, [visibleFields, customFieldDefs]);
  const show = (key) => fields.has(key);

  // Filter to only tasks with dates, sort by start
  const datedTasks = useMemo(() => {
    return tasks
      .filter(t => t.scheduled_start || t.due_date)
      .map(task => {
        let barStart, barEnd;
        if (task.scheduled_start && task.scheduled_end) {
          barStart = new Date(task.scheduled_start);
          barEnd = new Date(task.scheduled_end);
        } else if (task.due_date) {
          barStart = startOfDay(task.due_date);
          barEnd = new Date(barStart);
          const durationDays = Math.max(1, (task.duration || 60) / (8 * 60));
          barEnd.setDate(barEnd.getDate() + Math.ceil(durationDays));
        }
        // Ensure at least 1-day visual width
        if (barEnd - barStart < 24 * 60 * 60 * 1000) {
          barEnd = new Date(barStart.getTime() + 24 * 60 * 60 * 1000);
        }
        return { task, barStart, barEnd };
      })
      .sort((a, b) => a.barStart - b.barStart);
  }, [tasks]);

  // Compute timeline range - tight around the task data, with a small buffer.
  const { startDate, timelineDays } = useMemo(() => {
    const today = startOfDay(new Date());
    let earliest = today;
    let latest = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
    datedTasks.forEach(({ barStart, barEnd }) => {
      if (barStart < earliest) earliest = startOfDay(barStart);
      if (barEnd > latest) latest = new Date(barEnd);
    });
    // Add 3-day buffer on each side
    const start = new Date(earliest);
    start.setDate(start.getDate() - 3);
    const end = new Date(latest);
    end.setDate(end.getDate() + 3);
    const days = Math.max(14, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    return { startDate: start, endDate: end, timelineDays: days };
  }, [datedTasks]);

  // Build day cells
  const dayCells = useMemo(() => {
    const letters = weekdayLetters(0); // Sunday first, to index by getDay()
    const cells = [];
    for (let i = 0; i < timelineDays; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const isToday = date.toDateString() === new Date().toDateString();
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isFirstOfMonth = date.getDate() === 1 || i === 0;
      cells.push({
        date,
        isToday,
        isWeekend,
        isFirstOfMonth,
        dayNum: date.getDate(),
        monthLabel: date.toLocaleDateString(language, { month: 'short', year: 'numeric' }),
        weekday: letters[date.getDay()],
      });
    }
    return cells;
  }, [startDate, timelineDays, weekdayLetters, language]);

  // Scroll today into view on mount and when zoom changes
  useEffect(() => {
    if (!scrollRef.current) return;
    const today = startOfDay(new Date());
    const offsetDays = (today - startDate) / (1000 * 60 * 60 * 24);
    const targetX = Math.max(0, offsetDays * dayWidth - TASK_PANEL_WIDTH - 80);
    scrollRef.current.scrollLeft = targetX;
  }, [startDate, dayWidth]);

  const todayOffset = useMemo(() => {
    const today = startOfDay(new Date());
    return ((today - startDate) / (1000 * 60 * 60 * 24)) * dayWidth;
  }, [startDate, dayWidth]);

  const handleClick = useCallback((task) => {
    if (onTaskClick) onTaskClick(task);
    else openTaskDetail(task.id);
  }, [onTaskClick, openTaskDetail]);

  const totalGridWidth = timelineDays * dayWidth;
  const tasksAreaHeight = Math.max(datedTasks.length * ROW_HEIGHT, 200);

  return (
    <div className="flex flex-col h-[calc(100dvh-15rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] md:h-[calc(100vh-12rem)] bg-card rounded-lg border border-border overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-border shrink-0 bg-card">
        <span className="text-xs text-muted-foreground">{t('timeline.zoom') || 'Zoom'}:</span>
        <div className="flex items-center gap-1 bg-accent/60 rounded-lg p-0.5">
          {Object.entries(ZOOM_PRESETS).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => setZoom(key)}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                zoom === key
                  ? 'bg-amber-500 text-black font-medium'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              {t(preset.labelKey)}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground/70 ml-auto">
          {t('ui_views.timeline.tasks_shown', { shown: datedTasks.length, count: tasks.length })}
        </span>
      </div>

      {/* Body: scrollable horizontally; vertical scroll on inner */}
      <div ref={scrollRef} className="flex-1 overflow-auto relative">
        <div className="relative" style={{ width: TASK_PANEL_WIDTH + totalGridWidth, minHeight: '100%' }}>
          {/* ──── Header row (sticky top) ──── */}
          <div
            className="sticky top-0 z-30 flex bg-card border-b border-border"
            style={{ height: HEADER_HEIGHT }}
          >
            {/* Left header (task panel column) */}
            <div
              className="sticky left-0 z-40 bg-card border-r border-border flex items-end px-4 py-2"
              style={{ width: TASK_PANEL_WIDTH }}
            >
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('tasks.task_column')}</span>
            </div>
            {/* Day cells header */}
            <div className="flex" style={{ width: totalGridWidth }}>
              {dayCells.map((cell, i) => (
                <div
                  key={i}
                  className={`shrink-0 border-r border-border/40 flex flex-col items-center justify-end pb-1 ${
                    cell.isToday ? 'bg-amber-500/10' : cell.isWeekend ? 'bg-white/[0.02]' : ''
                  }`}
                  style={{ width: dayWidth }}
                >
                  {cell.isFirstOfMonth && (
                    <span className="text-[10px] text-muted-foreground font-medium absolute mt-1" style={{ top: 4 }}>
                      {cell.monthLabel}
                    </span>
                  )}
                  {dayWidth >= 60 && (
                    <span className="text-[9px] text-muted-foreground/70 leading-none">
                      {cell.weekday}
                    </span>
                  )}
                  <span className={`text-xs leading-tight ${cell.isToday ? 'text-amber-700 dark:text-amber-400 font-bold' : 'text-muted-foreground'}`}>
                    {cell.dayNum}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ──── Body row ──── */}
          <div className="flex" style={{ minHeight: tasksAreaHeight }}>
            {/* Sticky left: task name list */}
            <div
              className="sticky left-0 z-20 bg-card border-r border-border shrink-0"
              style={{ width: TASK_PANEL_WIDTH }}
            >
              {datedTasks.map(({ task }, i) => {
                const project = projects.find(p => p.id === task.project_id);
                const priority = PRIORITIES[task.priority] || PRIORITIES.p4;
                const isCompleted = task.status === 'completed';
                return (
                  <div
                    key={task.id}
                    onClick={() => handleClick(task)}
                    className={`flex items-center gap-2 px-4 cursor-pointer hover:bg-accent/60 transition-colors border-b border-border/40 ${i % 2 === 1 ? 'bg-white/[0.015]' : ''}`}
                    style={{ height: ROW_HEIGHT }}
                  >
                    <div
                      className="w-2 h-2 rounded-sm shrink-0"
                      style={{ backgroundColor: project?.color || '#6B7280' }}
                    />
                    <span className={`text-sm truncate ${isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'}`} title={task.title}>
                      {task.title}
                    </span>
                    {show('priority') && (
                      <span className={`ml-auto shrink-0 text-[10px] px-1.5 py-0.5 rounded ${priority.bgLight} ${priority.textColor}`}>
                        {t(priority.altLabelKey)}
                      </span>
                    )}
                  </div>
                );
              })}
              {datedTasks.length === 0 && (
                <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                  {t('timeline.empty') || 'No scheduled tasks yet'}
                </div>
              )}
            </div>

            {/* Right: timeline grid */}
            <div className="relative shrink-0" style={{ width: totalGridWidth }}>
              {/* Vertical grid lines + weekend background */}
              {dayCells.map((cell, i) => (
                <div
                  key={i}
                  className={`absolute top-0 bottom-0 border-r border-border/30 ${
                    cell.isWeekend ? 'bg-white/[0.015]' : ''
                  } ${cell.isToday ? 'bg-amber-500/[0.04]' : ''}`}
                  style={{ left: i * dayWidth, width: dayWidth }}
                />
              ))}

              {/* Horizontal row separators (zebra) */}
              {datedTasks.map((_, i) => (
                <div
                  key={`row-${i}`}
                  className={`absolute left-0 right-0 border-b border-border/40 ${i % 2 === 1 ? 'bg-white/[0.015]' : ''}`}
                  style={{ top: i * ROW_HEIGHT, height: ROW_HEIGHT }}
                />
              ))}

              {/* Today vertical line (above grid, below bars) */}
              <div
                className="absolute top-0 bottom-0 w-px bg-amber-400/70 z-10 shadow-[0_0_8px_rgba(245,158,11,0.5)]"
                style={{ left: todayOffset }}
              />

              {/* Task bars */}
              {datedTasks.map(({ task, barStart, barEnd }, i) => {
                const offsetDays = (barStart - startDate) / (1000 * 60 * 60 * 24);
                const widthDays = Math.max(0.5, (barEnd - barStart) / (1000 * 60 * 60 * 24));
                const left = offsetDays * dayWidth;
                const width = Math.max(28, widthDays * dayWidth);
                const priority = PRIORITIES[task.priority] || PRIORITIES.p4;
                const isCompleted = task.status === 'completed';
                const project = projects.find(p => p.id === task.project_id);
                return (
                  <div
                    key={task.id}
                    onClick={() => handleClick(task)}
                    className={`absolute rounded-md cursor-pointer flex items-center px-2 gap-1.5 text-xs font-medium transition-all hover:brightness-110 hover:shadow-lg overflow-hidden border z-20 ${
                      isCompleted ? 'opacity-50' : ''
                    } ${priority.bgLight} ${priority.borderColor || 'border-transparent'}`}
                    style={{
                      top: i * ROW_HEIGHT + 6,
                      left,
                      width,
                      height: ROW_HEIGHT - 12,
                    }}
                    title={`${task.title}${project ? ` - ${project.team_name ? `${project.team_name} / ` : ''}${project.name}` : ''} (${t(priority.altLabelKey)})`}
                  >
                    {project && (
                      <div
                        className="w-1.5 h-1.5 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: project.color || '#6B7280' }}
                      />
                    )}
                    <span className={`truncate ${priority.textColor}`}>
                      {task.title}
                    </span>
                    {show('duration') && task.duration && width > 100 && (
                      <span className={`ml-auto text-[10px] opacity-70 ${priority.textColor}`}>
                        {formatDuration(task.duration, t)}
                      </span>
                    )}
                  </div>
                );
              })}

              {/* Empty state */}
              {datedTasks.length === 0 && (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center text-muted-foreground">
                    <p className="text-sm">{t('timeline.empty') || 'No scheduled tasks'}</p>
                    <p className="text-xs mt-1">{t('timeline.empty_hint') || 'Schedule tasks to see them here'}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineView;
